import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { InputCard } from '@components/cards';
import { RADIO, VARIANT_L } from '@components/cards/input-card/src/input-card.type';
import { IconOfflineSale, IconPass, IconTicket } from '@components/icons';
import { ModalFooter } from '@components/modals/modal-footer';
import { ModalHeader } from '@components/modals/modal-header';
import {
  OFFLINE_SALES_SCOPE_EVENT,
  OFFLINE_SALES_SCOPE_PASS,
  OfflineSalesForm,
  OfflineSalesScope,
} from '@components/process-offline-sales/src/process-offline-sales.type';

const ScopeCardContent = ({
  icon: Icon,
  title,
  subtitle,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  subtitle: string;
}) => (
  <div className='flex flex-col gap-1 ml-4 text-left'>
    <div className='flex items-center gap-2'>
      <Icon className='w-5 h-5 text-primary' />
      <p className='text-primary text-subtitle-m'>{title}</p>
    </div>
    <p className='text-tertiary text-body-m'>{subtitle}</p>
  </div>
);

export const SelectScope = ({ onContinue }: { onContinue: () => void }) => {
  const { t } = useTranslation();
  const { watch, setValue } = useFormContext<OfflineSalesForm>();
  const scope = watch('scope');

  const toggleScope = (next: OfflineSalesScope) => {
    if (scope === next) return;
    setValue('scope', next, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
    if (next === OFFLINE_SALES_SCOPE_PASS) {
      setValue('eventId', '');
      setValue('offerId', '');
      setValue('saleType', '');
    } else {
      setValue('passId', '');
    }
  };

  return (
    <>
      <ModalHeader title={t('offers:offlineSalesTitle')} primaryIcon={IconOfflineSale} />
      <div className='flex flex-col gap-4 px-4 py-6 sm:px-20 sm:pb-12'>
        <p className='text-body-m text-secondary'>{t('offers:offlineSalesScopeStepDescription')}</p>
        <div className='flex flex-col gap-3'>
          <InputCard
            type={RADIO}
            variant={VARIANT_L}
            isChecked={scope === OFFLINE_SALES_SCOPE_EVENT}
            isDisabled={false}
            handleClick={() => toggleScope(OFFLINE_SALES_SCOPE_EVENT)}>
            <ScopeCardContent
              icon={IconTicket}
              title={t('offers:offlineSalesScopeEventTitle')}
              subtitle={t('offers:offlineSalesScopeEventSubtitle')}
            />
          </InputCard>
          <InputCard
            type={RADIO}
            variant={VARIANT_L}
            isChecked={scope === OFFLINE_SALES_SCOPE_PASS}
            isDisabled={false}
            handleClick={() => toggleScope(OFFLINE_SALES_SCOPE_PASS)}>
            <ScopeCardContent
              icon={IconPass}
              title={t('offers:offlineSalesScopePassTitle')}
              subtitle={t('offers:offlineSalesScopePassSubtitle')}
            />
          </InputCard>
        </div>
      </div>
      <ModalFooter
        primaryButtonText={t('offers:continueButton')}
        onPrimaryClick={onContinue}
        isPrimaryDisabled={!scope}
      />
    </>
  );
};
